import { AppConfig, getAuthToken } from "@fxid/shared";

import {
	AnalyticsApi,
	AuthApi,
	Bank131Api,
	CrossPlayApi,
	DataConnectionsApi,
	FbinstantApi,
	FriendsApi,
	FtlIntegrationApi,
	GamesApi,
	GoApi,
	InfoApi,
	InviteApi,
	ManifestApi,
	NativeApi,
	PlayApi,
	ProfileApi,
	PublicGameConfigApi,
	PushTokensApi,
	StoreApi,
	SupportApi,
	WizqApi,
	YandexgamesApi} from "./gen/apis";
import { Configuration } from "./gen/runtime";
import { problemDetailsMiddleware } from "./ProblemDetailsMiddleware";

/**
 * Lazy initialization для openApiConfig чтобы избежать циклической зависимости
 * между @fxid/shared и @fxid/openapi-client
 */
let _openApiConfig: Configuration | null = null;

function getOpenApiConfig(): Configuration {
	if (!_openApiConfig) {
		_openApiConfig = new Configuration({
			basePath: AppConfig.apiBasePath,
			accessToken: getApiAccessToken,
			middleware: [problemDetailsMiddleware]
		});
	}
	return _openApiConfig;
}

/**
 * Экспортируемый объект конфигурации OpenAPI.
 * Использует Proxy для lazy initialization - конфигурация создается только при первом обращении.
 * Это необходимо для разрыва циклической зависимости между @fxid/shared и @fxid/openapi-client.
 */
export const openApiConfig = new Proxy({} as Configuration, {
	get(target, prop) {
		const config = getOpenApiConfig();
		const value = config[prop as keyof Configuration];

		// Если это метод, привязываем контекст
		if (typeof value === 'function') {
			return value.bind(config);
		}

		return value;
	}
});

function getApiAccessToken() {
	const token = getAuthToken();
	return token ?? "";
}

/**
 * Типы для API клиентов
 */
interface IOpenApiClient {
	Invite: InviteApi;
	Analytics: AnalyticsApi;
	DataConnections: DataConnectionsApi;
	Manifest: ManifestApi;
	Native: NativeApi;
	Auth: AuthApi;
	Games: GamesApi;
	Play: PlayApi;
	Profile: ProfileApi;
	Store: StoreApi;
	Info: InfoApi;
	YandexGames: YandexgamesApi;
	FacebookInstantGames: FbinstantApi;
	PublicGameConfigApi: PublicGameConfigApi;
	Friends: FriendsApi;
	Go: GoApi;
	Bank131: Bank131Api;
	WizQ: WizqApi;
	FtlIntegration: FtlIntegrationApi;
	Support: SupportApi;
	PushToken: PushTokensApi;
	CrossPlay: CrossPlayApi;
}

/**
 * Кэш для API клиентов
 */
const apiCache: Partial<IOpenApiClient> = {};

/**
 * Фабрика для создания API клиентов
 */
const apiFactories: Record<keyof IOpenApiClient, () => any> = {
	Invite: () => new InviteApi(getOpenApiConfig()),
	Analytics: () => new AnalyticsApi(getOpenApiConfig()),
	DataConnections: () => new DataConnectionsApi(getOpenApiConfig()),
	Manifest: () => new ManifestApi(getOpenApiConfig()),
	Native: () => new NativeApi(getOpenApiConfig()),
	Auth: () => new AuthApi(getOpenApiConfig()),
	Games: () => new GamesApi(getOpenApiConfig()),
	Play: () => new PlayApi(getOpenApiConfig()),
	Profile: () => new ProfileApi(getOpenApiConfig()),
	Store: () => new StoreApi(getOpenApiConfig()),
	Info: () => new InfoApi(getOpenApiConfig()),
	YandexGames: () => new YandexgamesApi(getOpenApiConfig()),
	FacebookInstantGames: () => new FbinstantApi(getOpenApiConfig()),
	PublicGameConfigApi: () => new PublicGameConfigApi(getOpenApiConfig()),
	Friends: () => new FriendsApi(getOpenApiConfig()),
	Go: () => new GoApi(getOpenApiConfig()),
	Bank131: () => new Bank131Api(getOpenApiConfig()),
	WizQ: () => new WizqApi(getOpenApiConfig()),
	FtlIntegration: () => new FtlIntegrationApi(getOpenApiConfig()),
	Support: () => new SupportApi(getOpenApiConfig()),
	PushToken: () => new PushTokensApi(getOpenApiConfig()),
	CrossPlay: () => new CrossPlayApi(getOpenApiConfig()),
};

/**
 * Proxy для lazy initialization API клиентов.
 * Каждый API клиент создается только при первом обращении к нему.
 */
const OpenApiClient = new Proxy({} as IOpenApiClient, {
	get(target, prop: string) {
		if (prop in apiFactories) {
			const key = prop as keyof IOpenApiClient;
			if (!apiCache[key]) {
				apiCache[key] = apiFactories[key]();
			}
			return apiCache[key];
		}
		return undefined;
	}
});

export default OpenApiClient;
